import React, { useState, useEffect, useRef } from 'react';
import { Camera, Calendar, Store, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useSelector } from 'react-redux';
import { setHandlers } from 'views/Stores/Table/BayDetails/ImageBboxes';
import ImageDetailsDialog from 'views/Stores/Table/BayDetails/DetailsDialog';
import { useParams } from 'react-router';
import { Grid } from '@mui/material';

// Real API function
const GetBaysWithImagePagination = async (data) => {
  try {
    const response = await fetch('https://disha-prod.azurewebsites.net/api/azure/get_bays_with_image_pagination', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.log('Error Calling GetBaysWithImagePagination API: ', error);
    throw error;
  }
};

const ImageCard = ({ capture, bay, index, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Get the metadata from capture
  const metadata = capture.metadata && capture.metadata.length > 0 ? capture.metadata[0] : {};
  
  // Get bay format to determine if it's SevenEleven
  const format = bay.format || '';
  const isSevenEleven = format === 'SevenEleven';

  // Calculate SEL Compliance Score (same logic as in ImageDetailsDialog)
  const calculateSELComplianceScore = (metadata, isSevenEleven) => {
    if (isSevenEleven) {
      const totalPop = metadata.total_pop || 0;
      const matched = metadata.pop_matching_result?.matched || 0;
      
      return totalPop > 0 ? ((matched / totalPop) * 100).toFixed(2) : 'N/A';
    } else {
      // For non-SevenEleven, use fullness as OSA score
      return metadata?.fullness ?? 'N/A';
    }
  };

  // Calculate detected objects count (same logic as ImageDetailsDialog)
  const calculateDetectedObjectsCount = (metadata) => {
    const detectedObjects = metadata.tray_det_bboxes || [];
    
    // Count occurrences of each category, excluding 'filled'
    const objectCounts = {};
    detectedObjects.forEach((box) => {
      const category = box.name || box.type || 'Unknown';
      if (category !== 'filled') { // Filter out 'filled' objects
        objectCounts[category] = (objectCounts[category] || 0) + 1;
      }
    });
    
    // Get total count from filtered objects
    let totalDetectedObjects = Object.values(objectCounts).reduce((sum, count) => sum + count, 0);
    
    // Add Allian Pop count if present
    if (metadata.allian_pop?.length > 0) {
      totalDetectedObjects += metadata.allian_pop.length;
    }
    
    return totalDetectedObjects;
  };

  // Check if there are missing items
  const hasMissingItems = () => {
    const missingDescriptions = metadata.missing_pop?.map((item) => item.vmDescription).filter(Boolean) ?? [];
    return missingDescriptions.length > 0;
  };

  // Format timestamp for display
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let dateObj;
      if (typeof timestamp === 'string') {
        dateObj = new Date(timestamp);
      } else if (typeof timestamp === 'object' && timestamp.seconds) {
        dateObj = new Date(timestamp.seconds * 1000);
      } else {
        dateObj = new Date(timestamp);
      }

      if (isNaN(dateObj.getTime())) {
        return 'N/A';
      }

      return dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'N/A';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let dateObj;
      if (typeof timestamp === 'string') {
        dateObj = new Date(timestamp);
      } else if (typeof timestamp === 'object' && timestamp.seconds) {
        dateObj = new Date(timestamp.seconds * 1000);
      } else {
        dateObj = new Date(timestamp);
      }

      if (isNaN(dateObj.getTime())) {
        return 'N/A';
      }

      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Get the actual timestamp
  const getTimestamp = (capture) => {
    if (capture.timestamps?.created_at) {
      return capture.timestamps.created_at;
    }
    if (capture.timestamp) {
      return capture.timestamp;
    }
    if (capture.created_at) {
      return capture.created_at;
    }
    if (capture.createdAt) {
      return capture.createdAt;
    }
    if (capture.date) {
      return capture.date;
    }
    // Also check in metadata
    if (metadata.timestamp) {
      return metadata.timestamp;
    }
    return null;
  };

  // Get username instead of email
  const getUserDisplayName = (userDetails) => {
    if (!userDetails) return 'System';
    
    if (userDetails.username) return userDetails.username;
    if (userDetails.name) return userDetails.name;
    if (userDetails.user_name) return userDetails.user_name;
    if (userDetails.email) {
      const emailUsername = userDetails.email.split('@')[0];
      return emailUsername;
    }
    return 'System';
  };

  // Calculate SEL compliance score
  const selComplianceScore = calculateSELComplianceScore(metadata, isSevenEleven);
  const detectedObjectsCount = calculateDetectedObjectsCount(metadata);
  const timestamp = getTimestamp(capture);
  const showMissingItemsWarning = hasMissingItems();

  // Get score color for the badge (same logic as ImageDetailsDialog)
  const getScoreColor = (score) => {
    if (score === 'N/A') return 'text-gray-600 bg-gray-50';
    
    const numericScore = parseFloat(score);
    if (numericScore >= 80) return 'text-green-600 bg-green-50';
    if (numericScore >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div 
      className="flex-shrink-0 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl group w-full min-w-[280px] max-w-[320px]"
      onClick={() => onClick(capture)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative">
        <img 
          src={capture.image_url} 
          alt={`Capture ${index + 1}`}
          className="w-full h-48 object-cover"
          onError={(e) => {
            if (capture.thumbnail_url && e.target.src !== capture.thumbnail_url) {
              e.target.src = capture.thumbnail_url;
            }
          }}
        />
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
          <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        </div>

        {/* Missing Items Warning Overlay */}
        {showMissingItemsWarning && isHovered && (
          <div className="absolute inset-0 bg-red-500 bg-opacity-30 transition-all duration-200">
            <div className="absolute top-3 left-3 bg-red-500 text-white p-1 rounded-full shadow-lg">
              <svg 
                className="w-4 h-4" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                  clipRule="evenodd" 
                />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Title and Bay Info */}
        <div className="mb-3 flex justify-between items-start">
          <h3 className="font-semibold text-gray-800 text-lg mb-1 truncate flex-1 mr-2">
            {bay.bay_id}
          </h3>
          <div className="text-gray-500 text-sm flex items-center flex-shrink-0">
            <Store className="w-3 h-3 mr-1" />
            <span className="truncate max-w-[100px]">{bay.fixture_name || 'Unknown Fixture'}</span>
          </div>
        </div>

        {/* SEL Compliance Score */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">SEL Compliance Score</span>
            <span className={`text-sm font-medium ${getScoreColor(selComplianceScore).split(' ')[0]}`}>
              {selComplianceScore !== 'N/A' ? `${selComplianceScore}%` : 'N/A'}
            </span>
          </div>
        </div>

        {/* Metadata Info */}
        <div className="space-y-2 mb-3">
          <div className="text-sm text-gray-600 flex items-center justify-between">
            <span>Objects Detected</span>
            <span className="font-medium text-gray-800">{detectedObjectsCount}</span>
          </div>
          
          {capture.userDetails && (
            <div className="text-sm text-gray-600 flex items-center justify-between">
              <span>Captured By</span>
              <span className="font-medium text-gray-800 truncate ml-2 max-w-[120px]">
                {getUserDisplayName(capture.userDetails)}
              </span>
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center flex-1 mr-2">
            <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
            <span className="truncate">{formatDate(timestamp)}</span>
          </div>
          <div className="flex items-center flex-shrink-0">
            <Clock className="w-3 h-3 mr-1" />
            <span>{formatTime(timestamp)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Horizontal Image Carousel Component
const ImageCarousel = ({ bay, onImageClick }) => {
  const carouselRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const hasCaptures = bay.captureDetails && bay.captureDetails.length > 0;

  // Responsive card dimensions
  const getCardDimensions = () => {
    const width = containerWidth || window.innerWidth;
    if (width < 480) return { cardWidth: 260, gap: 12 }; // mobile
    if (width < 640) return { cardWidth: 280, gap: 12 }; // small mobile
    if (width < 768) return { cardWidth: 300, gap: 16 }; // tablet
    if (width < 1024) return { cardWidth: 320, gap: 16 }; // small desktop
    return { cardWidth: 340, gap: 20 }; // large desktop
};

  const { cardWidth, gap } = getCardDimensions();
  const totalCardWidth = cardWidth + gap;

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      if (carouselRef.current) {
        const newContainerWidth = carouselRef.current.offsetWidth;
        setContainerWidth(newContainerWidth);
        
        if (hasCaptures) {
          const { cardWidth: newCardWidth, gap: newGap } = getCardDimensions();
          const newTotalCardWidth = newCardWidth + newGap;
          const totalContentWidth = bay.captureDetails.length * newTotalCardWidth;
          const newMaxScroll = Math.max(0, totalContentWidth - newContainerWidth);
          setMaxScroll(newMaxScroll);
          
          // Reset scroll position if it exceeds new max
          if (scrollPosition > newMaxScroll) {
            setScrollPosition(0);
            if (carouselRef.current) {
              carouselRef.current.scrollLeft = 0;
            }
          }
        }
      }
    };

    // Initial calculation
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Use ResizeObserver for more accurate container size changes
    let resizeObserver;
    if (carouselRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(carouselRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [hasCaptures, bay.captureDetails, scrollPosition]);

  // Calculate scroll amounts based on container size
  const getScrollAmount = () => {
    const visibleCards = Math.floor(containerWidth / totalCardWidth);
    return Math.max(1, visibleCards - 1) * totalCardWidth;
  };

  const handleScroll = (direction) => {
    if (!carouselRef.current) return;

    const scrollAmount = direction === 'left' ? -getScrollAmount() : getScrollAmount();
    const newPosition = Math.max(0, Math.min(scrollPosition + scrollAmount, maxScroll));

    carouselRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });

    setScrollPosition(newPosition);
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < maxScroll;

  if (!hasCaptures) {
    return (
      <div className="w-full mb-6 sm:mb-8">
        {/* Bay Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-0">{bay.bay_id}</h3>
          <div className="flex items-center text-gray-600">
            <Store className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm sm:text-lg truncate">{bay.fixture_name}</span>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-gray-500 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
          <Camera className="w-12 h-12 sm:w-16 sm:h-16 mb-4 text-gray-300" />
          <p className="text-center font-medium text-base sm:text-lg">No bay captures found</p>
          <p className="text-sm sm:text-md text-gray-400">for Bay ID: {bay.bay_id}</p>
          <p className="text-sm sm:text-md text-gray-400 text-center">{bay.fixture_name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-8 sm:mb-12 px-4">
      {/* Bay Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-0">{bay.bay_id}</h3>
          <div className="flex items-center text-gray-600">
            <Store className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm sm:text-lg truncate">{bay.fixture_name}</span>
          </div>
        </div>

      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center text-gray-700">
          <Camera className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
          <span className="font-medium text-sm sm:text-lg">{bay.captureDetails.length} Captures</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleScroll('left')}
            disabled={!canScrollLeft}
            className={`p-2 sm:p-3 rounded-full transition-colors duration-200 ${
              canScrollLeft
                ? 'bg-green-100 hover:bg-green-200 text-green-700 shadow-sm' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={() => handleScroll('right')}
            disabled={!canScrollRight}
            className={`p-2 sm:p-3 rounded-full transition-colors duration-200 ${
              canScrollRight
                ? 'bg-green-100 hover:bg-green-200 text-green-700 shadow-sm' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>

      {/* Horizontal scrolling container */}
      <div className="relative mb-6 overflow-hidden">
        <div 
          ref={carouselRef}
          className="flex overflow-x-auto scrollbar-hide pb-4"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' },
            gap: `${gap}px`
          }}
          onScroll={(e) => setScrollPosition(e.target.scrollLeft)}
        >
          {bay.captureDetails.map((capture, index) => (
            <ImageCard
              key={index}
              capture={capture}
              bay={bay}
              index={index}
              onClick={onImageClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const Gallery = () => {
  const [allPages, setAllPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentVisiblePage, setCurrentVisiblePage] = useState(1);

  console.log(currentVisiblePage);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [bayDetails, setBayDetails] = useState(null);
  

  const { store_id } = useParams();
  const selectedDate = useSelector((state) => state.customization.selectedDate);
  const formattedDate = selectedDate ? new Date(selectedDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);

  console.log('Gallery - store_id:', store_id);
  console.log('Gallery - formattedDate:', formattedDate);

  // Add ref to track the current request parameters to avoid stale closures
  const currentRequestRef = useRef({
    store_oid: store_id,
    dates: formattedDate
  });

  // Format timestamp function from BayDetails
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: 'N/A' };

    try {
      const dateObj = new Date(timestamp);
      const date = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const time = dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });

      return { date, time };
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return { date: 'N/A', time: 'N/A' };
    }
  };

  // Modal handlers
  const handleOpenDialog = (imageData) => {
    setSelectedImage(imageData);
    setDialogOpen(true);
    setImageLoaded(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
    setImageLoaded(false);
  };

  // Track which page is currently visible
  useEffect(() => {
    const handleScroll = () => {
      if (allPages.length === 0) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      
      // Find which page is currently most visible
      let visiblePage = 1;
      const pageElements = document.querySelectorAll('[data-page]');
      
      pageElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const pageNumber = parseInt(element.getAttribute('data-page'));
        
        // If the top of the page section is visible or the section takes up most of the viewport
        if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
          visiblePage = pageNumber;
        }
      });
      
      setCurrentVisiblePage(visiblePage);

      // Original scroll loading logic - check if we need to load more pages
      if (loading || !hasMore) return;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= documentHeight - 200) {
        fetchBaysData(currentPage + 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, loading, hasMore, allPages]);

  useEffect(() => {
    // Set up handlers for ImageWithBoundingBoxes component
    setHandlers(handleOpenDialog, setImageLoaded);

    // Listen for modal open events from image carousel
    const handleOpenImageModal = (event) => {
      const { bay, capture } = event.detail;
      // Convert the capture data to the expected format for ImageDetailsDialog
      const imageData = {
        image_url: capture.image_url,
        metadata: capture.metadata || [],
        userDetails: capture.userDetails || {},
        timestamps: capture.timestamps
      };
      
      // Create bayDetails structure for the dialog
      const bayDetailsForDialog = {
        data: [{
          bay_id: bay.bay_id,
          format: bay.format || '',
          captureDetails: [imageData]
        }]
      };
      
      setBayDetails(bayDetailsForDialog);
      handleOpenDialog(imageData);
    };

    window.addEventListener('openImageModal', handleOpenImageModal);
    
    return () => {
      window.removeEventListener('openImageModal', handleOpenImageModal);
    };
  }, []);

  // FIXED: Simplified useEffect that directly calls fetchBaysData with the current values
  useEffect(() => {
    // Update the ref with current values
    currentRequestRef.current = {
      store_oid: store_id,
      dates: formattedDate
    };

    // Reset all state for new date/store
    setAllPages([]);
    setCurrentPage(1);
    setCurrentVisiblePage(1);
    setHasMore(true);
    setTotalPages(0);
    setError(null);
    
    console.log('Date/Store changed - Fetching bays data with:', currentRequestRef.current);
    
    // Call fetchBaysData directly with current values instead of relying on state
    fetchBaysDataWithParams(currentRequestRef.current, 1);
  }, [formattedDate, store_id]);

  // FIXED: Separate function that takes parameters directly to avoid stale closure issues
  const fetchBaysDataWithParams = async (requestParams, page) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    if (!requestParams.store_oid) {
      setError('Store ID is missing. Please ensure the store is selected and the URL is correct.');
      setLoading(false);
      setHasMore(false);
      return;
    }

    if (!requestParams.dates) {
      setError('Date is missing. Please select a date from the calendar.');
      setLoading(false);
      setHasMore(false);
      return;
    }

    try {
      const apiPayload = {
        ...requestParams,
        page: page
      };
      
      console.log('API Call - Payload:', apiPayload);
      
      const response = await GetBaysWithImagePagination(apiPayload);
      
      console.log('API Response:', response);
      
      if (response && response.code === 200) {
        // Extract bay data from the nested response structure
        let bayData = [];
        
        if (response.data && response.data.bays) {
          bayData = Array.isArray(response.data.bays) ? response.data.bays : [response.data.bays];
        }
        
        // Extract pagination info
        const paginationInfo = response.data?.pagination;
        if (paginationInfo) {
          setTotalPages(paginationInfo.totalPages);
          setHasMore(paginationInfo.hasMore);
          console.log('Pagination info:', paginationInfo);
        }
        
        // If no data or empty array, stop pagination
        if (bayData.length === 0) {
          setHasMore(false);
          setLoading(false);
          return;
        }

        console.log('Processed bay data:', bayData);

        setAllPages(prevPages => {
          const existingPageIndex = prevPages.findIndex(p => p.pageNumber === page);
          if (existingPageIndex >= 0) {
            return prevPages;
          }
          
          const newPage = {
            pageNumber: page,
            data: bayData
          };
          
          const updatedPages = [...prevPages, newPage].sort((a, b) => a.pageNumber - b.pageNumber);
          return updatedPages;
        });
        
        setCurrentPage(page);
        
        // Use pagination info to determine if there are more pages
        if (paginationInfo && !paginationInfo.hasMore) {
          setHasMore(false);
        }
      } else {
        setError('Failed to fetch bays data: ' + (response?.message || 'Unknown error'));
        if (page === 1) {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Error fetching bays data:', err);
      setError('Error fetching data: ' + err.message);
      if (page === 1) {
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Updated fetchBaysData to use current request parameters
  const fetchBaysData = async (page) => {
    return fetchBaysDataWithParams(currentRequestRef.current, page);
  };

  return (
    
    <div className="w-full p-4">
      <div className="min-h-screen p-4 max-w-6xl mx-auto">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">Gallery View</h1>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className="space-y-8">
              {allPages.map((pageData) => (
                <div key={pageData.pageNumber} data-page={pageData.pageNumber}>
                  {/* Render bay carousels */}
                  {Array.isArray(pageData.data) && pageData.data.map((bay) => (
                    <ImageCarousel 
                      key={bay._id} 
                      bay={bay} 
                      onImageClick={(capture) => {
                        window.dispatchEvent(new CustomEvent('openImageModal', { 
                          detail: { bay, capture } 
                        }));
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </Grid>

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                <span className="ml-3 text-green-700 text-lg">Loading more pages...</span>
              </div>
            </Grid>
          )}

          {/* End of Content */}
          {!hasMore && allPages.length > 0 && (
            <Grid item xs={12}>
              <div className="text-center py-8">
                <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                  <h3 className="text-xl font-medium text-green-800 mb-2">End of Gallery</h3>
                  <p className="text-green-600">
                    Youve viewed all {totalPages} pages ({allPages.reduce((total, page) => total + (Array.isArray(page.data) ? page.data.length : 0), 0)} bays total)
                  </p>
                </div>
              </div>
            </Grid>
          )}

          {/* Error State */}
          {error && (
            <Grid item xs={12}>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-center mb-6">
                {error}
                <button 
                  onClick={() => fetchBaysData(currentPage)}
                  className="ml-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors duration-300"
                >
                  Retry
                </button>
              </div>
            </Grid>
          )}

          {/* Empty State */}
          {!loading && !error && allPages.length === 0 && (
            <Grid item xs={12}>
              <div className="text-center py-12">
                <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-600 mb-2">No bays found</h3>
                <p className="text-gray-500">No data available for the selected date.</p>
              </div>
            </Grid>
          )}
        </Grid>

        {/* ImageDetailsDialog from BayDetails */}
        <ImageDetailsDialog
          dialogOpen={dialogOpen}
          handleCloseDialog={handleCloseDialog}
          selectedImage={selectedImage}
          imageLoaded={imageLoaded}
          setImageLoaded={setImageLoaded}
          formatTimestamp={formatTimestamp}
          bayDetails={bayDetails}
        />
      </div>
    </div>
  );
};

export default Gallery;